/**
 * Team settings for office and advocacy teams (screenshots/119, brief §2.20; handoff starter TeamSettingsPage.tsx), the
 * Settings tab of a team account. Same layout as Settings (51): section nav (Team · You) and cards. Owners and admins
 * edit and every change saves at once (PUT /api/team/settings, versioned: on 409 the newer copy is shown); members see it
 * read-only. Offices are locked to their ward and to @cityofchicago.org (set by the Chicago Politics team).
 * Paints from snapshot:team-settings (or GET /api/team/settings). "Your account" and "Your notifications" are Settings (51)
 * at /dashboard/settings/you. "Members & roles" (the Access screen, 52, settings/Access.tsx) is its own page in the same
 * layout at /dashboard/settings/access (TeamAccessPage), for owners and admins.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import { Dropdown } from "../components/shared/Dropdown";
import { ListRow, RowMain, RowSub, RowTitle, RowTrail } from "../components/shared/List";
import { Switch } from "../components/shared/ui";
import type { OrgRole } from "../nav/navConfig";
import { toastStore } from "../stores/toastStore";
import { canEditTeam, teamSettingsProblems, type TeamSettings } from "./teamSettings";
import { Access } from "./Access";
import type { AccessSnapshot } from "../account/types";
import { useCrumbs } from "../app/crumbs";

export interface TeamSettingsSnapshot { settings: TeamSettings; role: OrgRole | null; members: number; alderperson?: string | null }
type SectionId = "team-profile" | "alert-defaults" | "team-digest" | "exports-links";
const SECTIONS: [string, { id: string; label: string; href?: string; manage?: boolean }[]][] = [
  ["Team", [{ id: "team-profile", label: "Team profile", href: "/dashboard/settings#team-profile" }, { id: "members", label: "Members & roles", href: "/dashboard/settings/access", manage: true },
    { id: "alert-defaults", label: "Alert defaults", href: "/dashboard/settings#alert-defaults" }, { id: "team-digest", label: "Team digest", href: "/dashboard/settings#team-digest" },
    { id: "exports-links", label: "Exports & links", href: "/dashboard/settings#exports-links" }]],
  ["You", [{ id: "account", label: "Your account", href: "/dashboard/settings/you#account" }, { id: "notifications", label: "Your notifications", href: "/dashboard/settings/you#notifications" }]],
];

/** The section nav (Team · You). Members & roles only for owners and admins (feature `access`), who manage the team. */
export function TeamSettingsNav({ current, role, onPage = false }: { current: string; role: OrgRole | null; onPage?: boolean }) {
  return (
    <nav className="settings-nav tset-nav" aria-label="Team settings">
      {SECTIONS.map(([group, items]) => (
        <div key={group} className="tset-nav__group"><span>{group}</span>
          {items.filter((i) => !i.manage || canEditTeam(role)).map((i) => (
            <a key={i.id} href={onPage && i.href?.startsWith("/dashboard/settings#") ? `#${i.id}` : i.href} aria-current={i.id === current ? "true" : undefined}>{i.label}</a>
          ))}</div>))}
    </nav>
  );
}

/** Members & roles: the Access screen inside Team settings (/dashboard/settings/access). */
export function TeamAccessPage({ team, initial }: { team: TeamSettingsSnapshot; initial: AccessSnapshot }) {
  useCrumbs([{ label: "Settings", href: "/dashboard/settings" }, { label: "Members & roles" }]);
  return (
    <div className="settings-page team-settings">
      <header className="tset-head"><h1 className="page-title">Team settings</h1><p>{team.settings.name}</p></header>
      <div className="settings-layout">
        <TeamSettingsNav current="members" role={team.role} />
        <Access initial={initial} embedded />
      </div>
    </div>
  );
}
const SECTION_NAMES: Record<TeamSettings["digest"]["sections"][number], string> = { tossups: "Toss-ups", filings: "Filings", wardResponses: "Ward responses", upcoming: "Upcoming votes" };

function Card({ id, title, sub, children }: { id: SectionId; title: string; sub: string; children: ReactNode }) {
  return (
    <section className="set-card tset-card" id={id} aria-labelledby={`${id}-title`}>
      <header className="set-card__head"><h2 id={`${id}-title`}>{title}</h2><p>{sub}</p></header>
      {children}
    </section>
  );
}
const Row = ({ title, text, children }: { title: string; text?: string; children: ReactNode }) => (
  <ListRow as="div" className="set-row"><RowMain className="set-row__text"><RowTitle as="b">{title}</RowTitle>{text && <RowSub wrap>{text}</RowSub>}</RowMain><RowTrail>{children}</RowTrail></ListRow>
);
/** The design's pill control (opens the shared Dropdown list). */
function Pill<T extends string>({ value, options, onChange, label, disabled }: { value: T; options: [T, string][]; onChange: (v: T) => void; label: string; disabled?: boolean }) {
  return (
    <Dropdown className="tset-pill" label={label} value={value} disabled={disabled} onChange={onChange} align="end" chevron={<ChevronDown size={14} aria-hidden />}
      options={options.map(([v, l]) => ({ value: v, label: l }))} />
  );
}

export function TeamSettingsPage({ initial }: { initial: TeamSettingsSnapshot | null }) {
  const [snap, setSnap] = useState(initial);
  const [name, setName] = useState(initial?.settings.name ?? "");
  const [open, setOpen] = useState(false);
  const saving = useRef(Promise.resolve());
  useEffect(() => { document.title = "Team settings · Chicago Politics"; }, []);
  useEffect(() => {
    if (snap) return;
    void fetch("/api/team/settings", { credentials: "include" }).then((r) => (r.ok ? r.json() : null)).then((d: TeamSettingsSnapshot | null) => { if (d) { setSnap(d); setName(d.settings.name); } }).catch(() => {});
  }, [snap]);
  if (!snap) return null;
  const s = snap.settings, edit = canEditTeam(snap.role), office = s.ward != null;

  /** Save at once (queued, so two quick changes don't race); a 409 brings the newer copy. */
  const save = (next: TeamSettings) => {
    if (!edit || teamSettingsProblems(next).length) return;
    setSnap({ ...snap, settings: next });
    saving.current = saving.current.then(async () => {
      const r = await fetch("/api/team/settings", { method: "PUT", credentials: "include", headers: { "content-type": "application/json" }, body: JSON.stringify({ version: next.version, settings: next }) }).catch(() => null);
      const body = r ? await r.json().catch(() => null) : null;
      if (r?.ok && body?.settings) setSnap((cur) => (cur ? { ...cur, settings: body.settings } : cur));
      else if (r?.status === 409 && body?.settings) { setSnap((cur) => (cur ? { ...cur, settings: body.settings } : cur)); setName(body.settings.name); toastStore.getState().show({ message: body.error }); }
      else toastStore.getState().show({ message: body?.error ?? "Couldn’t save. Try again." });
    });
  };
  const alert = (k: keyof TeamSettings["alertDefaults"], title: string, text: string) => (
    <Row title={title} text={text}><Switch on={s.alertDefaults[k]} label={title} onChange={(on) => save({ ...s, alertDefaults: { ...s.alertDefaults, [k]: on } })} /></Row>
  );
  const toggleSection = (k: TeamSettings["digest"]["sections"][number]) => {
    const has = s.digest.sections.includes(k);
    save({ ...s, digest: { ...s.digest, sections: has ? s.digest.sections.filter((x) => x !== k) : [...s.digest.sections, k] } });
  };

  return (
    <div className="settings-page team-settings">
      <header className="tset-head"><h1 className="page-title">Team settings</h1><p>{s.name}</p></header>
      <div className="settings-layout">
        <TeamSettingsNav current="team-profile" role={snap.role} onPage />
        <fieldset className="settings-cards tset-cards" disabled={!edit}>
          <Card id="team-profile" title="Team profile" sub="How your team appears to members and on shared links.">
            <Row title="Team name" text="Shown on shared dashboards and invites">
              <input className="tset-pill tset-name" aria-label="Team name" value={name} onChange={(e) => setName(e.target.value)}
                onBlur={() => name.trim() && name !== s.name && save({ ...s, name: name.trim() })} onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }} size={Math.max(8, name.length)} />
            </Row>
            {office && <Row title="Ward"><span className="tset-pill is-static">{`Ward ${s.ward}${snap.alderperson ? ` · Ald. ${snap.alderperson}` : ""}`}<ChevronDown size={14} aria-hidden /></span></Row>}
            {s.emailDomain && <Row title="Verified email domain" text="New members must sign up with this domain"><span className="tset-ok">{s.emailDomain} · verified</span></Row>}
          </Card>
          <Card id="alert-defaults" title="Alert defaults" sub="Members can turn these off for themselves.">
            {alert("forecastMoves", "Forecast moves on team items", "When a forecast for an item the team follows moves 10 points or more")}
            {alert("liveAlerts", "Live alerts", "When a team item is called during a live meeting")}
            {alert("newFilings", "New lobbying filings on team topics", "After each Board of Ethics update")}
            {office && alert("wardMilestones", "Ward responses milestones", `When “How would you vote?” for Ward ${s.ward} passes 100 answers on an item`)}
          </Card>
          <Card id="team-digest" title="Team digest" sub="One email for the whole team, so nobody misses the week.">
            <Row title="Send to" text="Mondays at 7 AM Chicago time, only when something moved">
              <Pill label="Send to" value={s.digest.to} onChange={(to) => save({ ...s, digest: { ...s.digest, to } })}
                options={[["all", `All ${snap.members} member${snap.members === 1 ? "" : "s"}`], ["admins", "Admins only"], ["off", "Off"]]} />
            </Row>
            <Row title="Include" text={s.digest.sections.map((k, i) => (i ? SECTION_NAMES[k].toLowerCase() : SECTION_NAMES[k])).join(", ")}>
              <span className="tset-multi">
                <button type="button" className="tset-pill" aria-expanded={open} onClick={() => setOpen(!open)}>{`${s.digest.sections.length} section${s.digest.sections.length === 1 ? "" : "s"}`}<ChevronDown size={14} aria-hidden /></button>
                {open && <span className="tset-menu" role="group" aria-label="Include">{(Object.keys(SECTION_NAMES) as (keyof typeof SECTION_NAMES)[]).map((k) => (
                  <label key={k}><input type="checkbox" checked={s.digest.sections.includes(k)} onChange={() => toggleSection(k)} />{SECTION_NAMES[k]}</label>))}</span>}
              </span>
            </Row>
          </Card>
          <Card id="exports-links" title="Exports & links" sub="Shared links never include team-only widgets.">
            <Row title="Who can export CSV, PNG and PDF" text="Applies to charts, tables and dashboards">
              <Pill label="Who can export CSV, PNG and PDF" value={s.exportsBy} onChange={(exportsBy) => save({ ...s, exportsBy })} options={[["members", "Admins and members"], ["admins", "Admins only"]]} />
            </Row>
            <Row title="Who can make public links" text="Anyone-with-the-link dashboards">
              <Pill label="Who can make public links" value={s.publicLinksBy} onChange={(publicLinksBy) => save({ ...s, publicLinksBy })} options={[["admins", "Owners and admins"], ["owners", "Owners only"]]} />
            </Row>
            <Row title="Delete team data" text="Notes, meeting log and ward-response settings. Public records stay.">
              <button type="button" className="tset-danger" disabled>Delete…</button>
            </Row>
          </Card>
        </fieldset>
      </div>
    </div>
  );
}
